import React, { useState, useEffect } from 'react';
import { db } from '../services/FishingDB';

interface StatsData {
  totalCatches: number;
  totalSessions: number;
  totalAreas: number;
  successRate: number;
  speciesBreakdown: { species: string; count: number; percentage: number }[];
  catchesByDate: { date: string; count: number }[];
  topAreas: { name: string; catches: number; sessions: number }[];
  averageCatchesPerSession: number;
  mostProductiveTime: { hour: number; count: number } | null;
}

export const StatsPage: React.FC = () => {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setIsLoading(true);

      const [catches, sessions, areas] = await Promise.all([
        db.catches.toArray(),
        db.sessions.toArray(),
        db.areas.toArray()
      ]);

      // Total counts
      const totalCatches = catches.length;
      const totalSessions = sessions.length;
      const totalAreas = areas.length;

      // Success rate (caught vs contact)
      const caughtCount = catches.filter(c => c.caught).length;
      const successRate = totalCatches > 0 ? (caughtCount / totalCatches) * 100 : 0;

      // Species breakdown
      const speciesCounts = new Map<string, number>();
      catches.forEach(c => {
        speciesCounts.set(c.species, (speciesCounts.get(c.species) || 0) + 1);
      });
      const speciesBreakdown = Array.from(speciesCounts.entries())
        .map(([species, count]) => ({
          species,
          count,
          percentage: (count / totalCatches) * 100
        }))
        .sort((a, b) => b.count - a.count);

      // Catches by date (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const recentCatches = catches.filter(c => new Date(c.timestamp) >= thirtyDaysAgo);
      const catchesByDateMap = new Map<string, number>();
      
      recentCatches.forEach(c => {
        const date = new Date(c.timestamp).toISOString().split('T')[0];
        catchesByDateMap.set(date, (catchesByDateMap.get(date) || 0) + 1);
      });
      
      const catchesByDate = Array.from(catchesByDateMap.entries())
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => a.date.localeCompare(b.date));

      // Top areas (with session count calculated)
      const areaSessionCounts = new Map<string, number>();
      sessions.forEach(s => {
        // Count sessions per area by checking catches
        const sessionCatches = catches.filter(c => c.sessionId === s.id);
        sessionCatches.forEach(c => {
          if (c.fishingAreaId) {
            areaSessionCounts.set(
              c.fishingAreaId, 
              (areaSessionCounts.get(c.fishingAreaId) || 0) + 1
            );
          }
        });
      });

      const topAreas = areas
        .map(area => ({
          name: area.name,
          catches: area.totalCatches || 0,
          sessions: areaSessionCounts.get(area.id) || 0
        }))
        .sort((a, b) => b.catches - a.catches)
        .slice(0, 5);

      // Average catches per session
      const averageCatchesPerSession = totalSessions > 0 
        ? Math.round((totalCatches / totalSessions) * 10) / 10 
        : 0;

      // Most productive time of day
      const hourCounts = new Map<number, number>();
      catches.forEach(c => {
        const hour = new Date(c.timestamp).getHours();
        hourCounts.set(hour, (hourCounts.get(hour) || 0) + 1);
      });
      
      let mostProductiveTime = null;
      if (hourCounts.size > 0) {
        const sorted = Array.from(hourCounts.entries())
          .sort((a, b) => b[1] - a[1]);
        const [hour, count] = sorted[0];
        mostProductiveTime = { hour, count };
      }

      setStats({
        totalCatches,
        totalSessions,
        totalAreas,
        successRate,
        speciesBreakdown,
        catchesByDate,
        topAreas,
        averageCatchesPerSession,
        mostProductiveTime
      });
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>📊 Statistik</h1>
        </div>
        <div style={styles.loading}>Laddar statistik...</div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>📊 Statistik</h1>
        </div>
        <div style={styles.emptyState}>
          <p>Kunde inte ladda statistik</p>
        </div>
      </div>
    );
  }

  if (stats.totalCatches === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>📊 Statistik</h1>
        </div>
        <div style={styles.emptyState}>
          <p>Inga fångster registrerade ännu</p>
          <p style={{ fontSize: '14px', color: '#94a3b8', marginTop: '8px' }}>
            Börja logga fångster för att se statistik!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>📊 Statistik</h1>
      </div>

      <div style={styles.content}>
        {/* Overview Cards */}
        <div style={styles.cardGrid}>
          <div style={styles.statCard}>
            <div style={styles.statIcon}>🎣</div>
            <div style={styles.statValue}>{stats.totalCatches}</div>
            <div style={styles.statLabel}>Totalt Fångster</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statIcon}>📝</div>
            <div style={styles.statValue}>{stats.totalSessions}</div>
            <div style={styles.statLabel}>Fiskepass</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statIcon}>📍</div>
            <div style={styles.statValue}>{stats.totalAreas}</div>
            <div style={styles.statLabel}>Fiskeplaster</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statIcon}>✅</div>
            <div style={styles.statValue}>{stats.successRate.toFixed(1)}%</div>
            <div style={styles.statLabel}>Landningsgrad</div>
          </div>
        </div>

        {/* Average Catches per Session */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>🎯 Genomsnitt</h2>
          <div style={styles.card}>
            <div style={styles.averageBox}>
              <span style={styles.averageValue}>{stats.averageCatchesPerSession}</span>
              <span style={styles.averageLabel}>fångster per fiskepass</span>
            </div>
          </div>
        </div>

        {/* Most Productive Time */}
        {stats.mostProductiveTime && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>⏰ Bästa Tiden</h2>
            <div style={styles.card}>
              <div style={styles.timeBox}>
                <span style={styles.timeValue}>
                  {stats.mostProductiveTime.hour.toString().padStart(2, '0')}:00
                </span>
                <span style={styles.timeLabel}>
                  {stats.mostProductiveTime.count} fångster
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Species Breakdown */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>🐟 Arter</h2>
          <div style={styles.card}>
            {stats.speciesBreakdown.map((item, index) => (
              <div key={item.species} style={styles.speciesItem}>
                <div style={styles.speciesInfo}>
                  <span style={styles.speciesName}>{item.species}</span>
                  <span style={styles.speciesCount}>
                    {item.count} ({item.percentage.toFixed(1)}%)
                  </span>
                </div>
                <div style={styles.progressBar}>
                  <div 
                    style={{
                      ...styles.progressFill,
                      width: `${item.percentage}%`,
                      backgroundColor: getColorForIndex(index)
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Catches by Date (Last 30 days) */}
        {stats.catchesByDate.length > 0 && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>📈 Senaste 30 Dagarna</h2>
            <div style={styles.card}>
              <div style={styles.chartContainer}>
                {stats.catchesByDate.map((item) => (
                  <div key={item.date} style={styles.barWrapper}>
                    <div style={styles.barLabel}>
                      {new Date(item.date).toLocaleDateString('sv-SE', { 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </div>
                    <div style={styles.barContainer}>
                      <div 
                        style={{
                          ...styles.bar,
                          height: `${(item.count / Math.max(...stats.catchesByDate.map(d => d.count))) * 100}%`
                        }}
                      />
                    </div>
                    <div style={styles.barValue}>{item.count}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Top Areas */}
        {stats.topAreas.length > 0 && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>🏆 Bästa Platserna</h2>
            <div style={styles.card}>
              {stats.topAreas.map((area, index) => (
                <div key={area.name} style={styles.areaItem}>
                  <div style={styles.areaRank}>{index + 1}</div>
                  <div style={styles.areaInfo}>
                    <div style={styles.areaName}>{area.name}</div>
                    <div style={styles.areaStats}>
                      {area.catches} fångster · {area.sessions} pass
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const getColorForIndex = (index: number): string => {
  const colors = [
    '#06b6d4', // cyan
    '#3b82f6', // blue
    '#8b5cf6', // purple
    '#ec4899', // pink
    '#f59e0b', // amber
    '#10b981', // green
  ];
  return colors[index % colors.length];
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#0f172a',
    paddingBottom: '80px', // Space for navigation
  },
  header: {
    padding: '16px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    backgroundColor: 'rgba(15, 23, 42, 0.95)',
    backdropFilter: 'blur(10px)',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 'bold',
    color: 'white',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px',
  },
  loading: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#94a3b8',
  },
  emptyState: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#94a3b8',
    textAlign: 'center',
    padding: '32px',
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
    marginBottom: '24px',
  },
  statCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '12px',
    padding: '16px',
    textAlign: 'center',
  },
  statIcon: {
    fontSize: '32px',
    marginBottom: '8px',
  },
  statValue: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#06b6d4',
    marginBottom: '4px',
  },
  statLabel: {
    fontSize: '12px',
    color: '#94a3b8',
  },
  section: {
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: 'white',
    marginBottom: '12px',
    marginLeft: '4px',
  },
  card: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '12px',
    padding: '16px',
  },
  averageBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  averageValue: {
    fontSize: '48px',
    fontWeight: 'bold',
    color: '#06b6d4',
  },
  averageLabel: {
    fontSize: '14px',
    color: '#94a3b8',
  },
  timeBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  timeValue: {
    fontSize: '48px',
    fontWeight: 'bold',
    color: '#06b6d4',
  },
  timeLabel: {
    fontSize: '14px',
    color: '#94a3b8',
  },
  speciesItem: {
    marginBottom: '16px',
  },
  speciesInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
  speciesName: {
    color: 'white',
    fontSize: '14px',
    fontWeight: '500',
  },
  speciesCount: {
    color: '#94a3b8',
    fontSize: '14px',
  },
  progressBar: {
    height: '8px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    transition: 'width 0.3s ease',
    borderRadius: '4px',
  },
  chartContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '8px',
    height: '200px',
    overflowX: 'auto',
    overflowY: 'hidden',
    padding: '8px 0',
  },
  barWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: '40px',
    flex: '0 0 auto',
  },
  barLabel: {
    fontSize: '10px',
    color: '#94a3b8',
    marginBottom: '4px',
    textAlign: 'center',
    writingMode: 'vertical-rl',
    transform: 'rotate(180deg)',
    height: '60px',
  },
  barContainer: {
    flex: 1,
    width: '30px',
    display: 'flex',
    alignItems: 'flex-end',
    position: 'relative',
  },
  bar: {
    width: '100%',
    backgroundColor: '#06b6d4',
    borderRadius: '4px 4px 0 0',
    minHeight: '4px',
    transition: 'height 0.3s ease',
  },
  barValue: {
    fontSize: '10px',
    color: '#06b6d4',
    marginTop: '4px',
    fontWeight: 'bold',
  },
  areaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    borderRadius: '8px',
    marginBottom: '8px',
  },
  areaRank: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: '#06b6d4',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    fontWeight: 'bold',
    flexShrink: 0,
  },
  areaInfo: {
    flex: 1,
  },
  areaName: {
    color: 'white',
    fontSize: '14px',
    fontWeight: '500',
    marginBottom: '4px',
  },
  areaStats: {
    color: '#94a3b8',
    fontSize: '12px',
  },
};
